import {
  Badge,
  Clear,
  Delete,
  Edit,
  Lock,
  LockOpen,
  Person,
  Save,
  Sell,
  Settings,
  Star,
  StarOutline,
  SupervisorAccount,
  WatchLaterOutlined,
  AppRegistration,
  AddToDrive,
  DeviceHub,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material'
import { Box, Button, Grid, IconButton, Stack, Typography, useMediaQuery, useTheme, Paper } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useNotification, useEditMode } from '../../store/redux/hooks'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import {
  ProjectAcronym,
  ProjectDetails,
  useDeleteProjectMutation,
  useEditProjectMutation,
  useGetAcronymsQuery,
  useLazyGetIsProjectFavoriteForUserQuery,
  useLazyGetProjectByIdQuery,
  useSetIsProjectFavoriteForUserMutation,
} from '../../store/redux/reducers/projectsSlice'
import { useGetLightProjectLeadersQuery } from '../../store/redux/reducers/usersSlice'
import { StyledContainerRowBetween } from '../../styles/ContainerStyles'
import { StyledPageHeaderContainer } from '../../styles/PageHeaderStyles'
import { StyledPageContent } from '../../styles/PageStyles'
import { breadcrumbsItemsActions } from '../../store/redux/reducers/breadcrumbsSlice'
import dayjs, { Dayjs } from 'dayjs'
import DateFormat from '../../store/types/enums/DateFormat'
import DetailsTile from '../../components/Tile/DetailsTile'
import { useSelector } from 'react-redux'
import { getCurrentUser, getIsManagementOrAdministrator } from '../../store/redux/reducers/currentUserSlice'
import { createErrorMessage } from '../../store/utils'
import AvatarComponent from '../../components/avatar/AvatarComponent'
import ProjectPicture from '../../components/avatar/ProjectPicture'
import ProjectStatus from '../../store/types/enums/ProjectStatus'
import TextInput from '../../components/Form/Inputs/TextInput'
import SelectInput, { Option } from '../../components/Form/Inputs/SelectInput'
import AutocompleteInput from '../../components/Form/Inputs/AutocompleteInput'
import CheckboxInput from '../../components/Form/Inputs/CheckboxInput'
import { LoadingButton } from '@mui/lab'
import Loading from '../../components/Loading/Loading'
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter'
import SelectProjectLeaders from '../../components/Form/SelectProjectLeaders'
import EmployeeContributionDrawer from './EmployeeContributionDrawer'
import ProjectCostsDrawer from './ProjectCostsDrawer'
import TeamTable from '../../components/Table/TeamTable'
import User from '../../store/types/models/User'
import { projectDefaultFormValues, ProjectFormValues } from '../../store/types/form/ProjectFormValues'
import sortByField from '../../store/utils/sortByField'
import DiscardChangesDialog from '../../components/Dialog/DiscardChangesDialog'
import DeleteProjectDialog from '../../components/Dialog/DeleteProjectDialog'
import { changeNullsToEmptyString } from '../../store/utils/falsyValuesConverter'
import { isUrl } from '../../store/utils/isUrlUtils'
import checkAccessDenied from '../../store/utils/checkAccessDenied'
import UnauthorizedView from '../UnauthorizedView/UnauthorizedView'
import { filterOptions } from '../../store/utils/filterProjectsAcronymsOptions'
import { RootState } from '../../store/redux/store'

const ProjectDetailsView = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const currentUser = useSelector(getCurrentUser)
  const isManagementOrAdministrator = useSelector((state: RootState) => getIsManagementOrAdministrator(state))
  const { id: projectId } = useParams()
  const [projectDetails, setProjectDetails] = useState<ProjectDetails | undefined>()
  const [showHours, setShowHours] = useState<boolean>(false)
  const theme = useTheme()
  const isXsMidSize = useMediaQuery(theme.breakpoints.down('xsMid'))
  const isMediumSize = useMediaQuery(theme.breakpoints.down('md'))
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isLargeSize = useMediaQuery(theme.breakpoints.down('lg'))
  const { displayNotification } = useNotification()
  const [deleteProject] = useDeleteProjectMutation()
  const { data: leadersData } = useGetLightProjectLeadersQuery({ page: 0, size: 20 })
  const [setIsProjectFavoriteForUser] = useSetIsProjectFavoriteForUserMutation()
  const [editProject] = useEditProjectMutation()
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [getProjectById] = useLazyGetProjectByIdQuery()
  const [getIsProjectFavorite] = useLazyGetIsProjectFavoriteForUserQuery()
  const [isFavorite, setIsFavorite] = useState(false)
  const [isNotFound, setIsNotFound] = useState<boolean>(false)
  const [isUnauthorized, setIsUnauthorized] = useState<boolean>(false)
  const [isEditMode, setEditMode] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs())
  const [page, setPage] = useState(0)
  const [searchedAcronym, setSearchedAcronym] = useState('')
  const { setIsEditMode } = useEditMode()
  const projectsProps = useMemo(() => {
    return { page: page, size: 20, acronym: searchedAcronym || undefined, sortByWorkHours: false, parent: 'null' }
  }, [page, searchedAcronym])

  const totalHours = useMemo(() => projectDetails?.totalHours ?? 0, [projectDetails])
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const { data: projects } = useGetAcronymsQuery(projectsProps)
  const [displayedProjects, setDisplayedProjects] = useState<ProjectAcronym[]>([])
  const [avatar, setAvatar] = useState<string | undefined>()
  const [extension, setExtension] = useState<string | undefined>()
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      setSearchedAcronym(value)
    }, 500)
  }, [])

  const onSelectBottomScroll = () => {
    if (!projects?.last) setPage(prev => prev + 1)
  }

  useEffect(() => {
    if (projects) {
      setDisplayedProjects(prevProjects => {
        const existingProjectIds = new Set(prevProjects.map(project => project.id))
        const newProjects = projects.content.filter(project => !existingProjectIds.has(project.id))
        return [...prevProjects, ...newProjects].filter(project => project.id !== projectId)
      })
    }
  }, [projects, projectId])

  const leaders = useMemo(() => {
    return leadersData ? leadersData.content : []
  }, [leadersData])

  const { handleSubmit, control, formState, reset, getValues, setValue, watch } = useForm<ProjectFormValues>({
    mode: 'onChange',
    defaultValues: async () => {
      const result = await getProjectById({
        id: projectId!!,
        date: dayjs(selectedDate)?.format(DateFormat.YearMonth),
      })

      if (result.isSuccess) {
        setProjectDetails(result.data)
        const project = result.data.project

        dispatch(breadcrumbsItemsActions.setInitialState())
        dispatch(breadcrumbsItemsActions.setSelectedItems({ projects: result.data.project.name }))

        return {
          ...project,
          leaders: project.leaders ? sortByField(project.leaders, 'lastName') : [],
          parent: project.parent ? { value: project.parent.id, label: project.parent.name } : null,
          team: project.team ? sortByField(project.team, 'lastName') : [],
        }
      } else if (checkAccessDenied(result.error)) {
        setIsUnauthorized(true)
      } else if (result.error !== undefined && 'status' in result?.error && result.error.status === 404) {
        setIsNotFound(true)
      }

      return projectDefaultFormValues
    },
  })

  const watchProjectStatus = watch('projectStatus')

  useEffect(() => {
    if (
      watchProjectStatus &&
      watchProjectStatus !== ProjectStatus.ACTIVE &&
      watchProjectStatus !== ProjectStatus.TRIAL
    ) {
      setValue('loggable', false)
    }
  }, [watchProjectStatus, setValue])

  const navigate = useNavigate()
  const { isSubmitting, isDirty } = formState
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(false)

  useEffect(() => {
    dispatch(setPageName(t('page.name.projectDetails')))
    dispatch(setPageIndex(PageIndex.PROJECTS))
  }, [dispatch, t])

  useEffect(() => {
    const loadData = async () => {
      const result = await getProjectById({
        id: projectId!!,
        date: dayjs(selectedDate)?.format(DateFormat.YearMonth),
      })

      if (result.isSuccess) {
        setProjectDetails(result.data)
        const project = result.data.project

        dispatch(breadcrumbsItemsActions.setInitialState())
        dispatch(breadcrumbsItemsActions.setSelectedItems({ projects: result.data.project.name }))

        const newFormValues = {
          ...project,
          leader: project.leaders ? project.leaders : [],
          parent: project.parent ? { value: project.parent.id, label: project.parent.name } : null,
        }

        reset(newFormValues)
      }
    }

    if (dayjs(selectedDate)) {
      loadData()
    }
  }, [selectedDate, reset, dispatch, getProjectById, projectId])

  useEffect(() => {
    formState.isValid ? setSaveButtonDisabled(false) : setSaveButtonDisabled(true)
  }, [formState.isValid])

  const handleDateChange = (e: dayjs.Dayjs | null) => {
    setSelectedDate(e ? dayjs(e.format(DateFormat.YearMonth)) : dayjs())
  }

  useEffect(() => {
    const loadIsFavorite = async () => {
      const result = await getIsProjectFavorite({
        projectId: projectDetails!!.project.id,
        employeeId: currentUser!!.id,
      })

      if (result.isSuccess) {
        setIsFavorite(result.data)
      }
    }

    if (currentUser && currentUser.id && projectDetails && projectDetails.project) {
      loadIsFavorite()
    }
  }, [currentUser, projectDetails, getIsProjectFavorite])

  const handleEdit = async (data: ProjectFormValues) => {
    const newData = {
      id: projectId!!,
      project: {
        ...data,
        parentId: data.parent?.value,
        leaders: data.leaders?.map(leader => ({
          projectId: projectId!!,
          leaderId: leader.id,
          main: leader.main ?? false,
        })),
        description: data.description === '' ? undefined : data.description,
        version: projectDetails?.project.version,
      },
    }
    try {
      const returnData = await editProject(newData).unwrap()
      setProjectDetails(prevState => {
        if (!prevState) return prevState

        return {
          ...prevState,
          project: changeNullsToEmptyString(returnData),
        }
      })

      setEditMode(false)
      setIsEditMode(false)
      reset(data)
      displayNotification({
        message: t('page.projects.notificationPopup.projectEdited'),
        type: 'success',
      })
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({
        message: `${t('page.users.message.error')} ${errorDescription}`,
        type: 'error',
      })
      reset(data)
    }
  }

  const handleFormSubmit = async (data: ProjectFormValues) => {
    await handleEdit(data)
  }

  const updateProjectStatus = (newStatus: any) => {
    setProjectDetails(prevState => {
      if (prevState) {
        return {
          ...prevState,
          project: {
            ...prevState.project,
            projectStatus: newStatus,
          },
        }
      } else {
        return prevState
      }
    })
  }

  const setProjectFavorite = async (newIsFavorite: boolean) => {
    if (!projectDetails || !projectDetails.project || !currentUser) return

    try {
      await setIsProjectFavoriteForUser({
        projectId: projectDetails.project.id,
        employeeId: currentUser!!.id,
        isFavorite: newIsFavorite,
      }).unwrap()
      setIsFavorite(newIsFavorite)
    } catch (_) {
      displayNotification({
        message: t('page.projects.notificationPopup.error'),
        type: 'error',
      })
    }
  }

  const handleDelete = async () => {
    if (projectDetails !== undefined) {
      if (projectDetails.project.projectStatus === ProjectStatus.DELETED.toUpperCase()) {
        return
      }

      try {
        await deleteProject(projectDetails.project.id).unwrap()
        setValue('projectStatus', 'DELETED')
        updateProjectStatus(ProjectStatus.DELETED.toUpperCase())
        setOpenDeleteDialog(false)
        displayNotification({
          message: t('page.projects.notificationPopup.projectRemoved'),
          type: 'success',
        })
        navigate('/projects')
      } catch (_) {
        setOpenDeleteDialog(false)
        displayNotification({
          message: t('page.projects.notificationPopup.error'),
          type: 'error',
        })
      }
    }
  }

  const isNotLeaderOfProject = useMemo(() => {
    return projectDetails
      ? (!projectDetails.project.leaders ||
          !projectDetails.project.leaders.some(leader => leader.id === currentUser?.id)) &&
          !isManagementOrAdministrator
      : false
  }, [projectDetails, currentUser?.id, isManagementOrAdministrator])

  useEffect(() => {
    if (projectDetails) {
      updateViewValues(projectDetails.project.avatar, projectDetails.project.extension)
    }
  }, [projectDetails])

  const updateViewValues = (avatar: string | undefined, extension: string | undefined) => {
    setAvatar(avatar)
    setExtension(extension)
  }

  const projectStatusOptions: Option[] = [
    { value: ProjectStatus.ACTIVE, label: t('page.projects.filterPopup.active') },
    { value: ProjectStatus.INACTIVE, label: t('page.projects.filterPopup.inactive') },
    { value: ProjectStatus.TRIAL, label: t('page.projects.filterPopup.trial') },
    { value: ProjectStatus.FINISHED, label: t('page.projects.filterPopup.finished') },
  ]

  const { replace } = useFieldArray({
    name: 'team',
    control,
  })

  const replaceArray = (team: User[]) => {
    return replace(sortByField(team, 'lastName'))
  }

  const handleCancelButtonClick = () => {
    if (!isDirty) {
      setEditMode(false)
      setIsEditMode(false)
    } else {
      setIsCancelDialogOpen(true)
    }
  }

  const resetForm = () => {
    reset()
    setEditMode(false)
    setIsEditMode(false)
    if (projectDetails) {
      updateViewValues(projectDetails.project.avatar, projectDetails.project.extension)
    }
  }

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const checkUnsavedChanges = () => {
    return isDirty || watch('avatar') !== formState.defaultValues?.avatar
  }

  const hoursRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!showHours) return
    const handleClickOutside = (e: MouseEvent) => {
      if (hoursRef.current && !hoursRef.current.contains(e.target as Node)) {
        setShowHours(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showHours])

  return (
    <StyledPageContent sx={{ display: 'flex', flexDirection: 'column', pl: isXsMidSize ? 0 : 'auto' }}>
      {!projectDetails ? (
        isNotFound ? (
          <Box width={'100%'} height={'100%'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
            {t('page.projectDetails.projectDeleted')}
          </Box>
        ) : isUnauthorized ? (
          <UnauthorizedView />
        ) : (
          <Box width={'100%'} height={'100%'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <Loading />
          </Box>
        )
      ) : (
        <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
          <StyledPageHeaderContainer
            sx={{
              mb: isMediumSize ? 2 : 6,
              mt: 2,
              pl: 0,
              pr: isMediumSize ? 0 : 2,
              width: isMediumSize && !isXsMidSize ? 'fit-content' : '100%',
              height: 'auto',
              mx: isXsMidSize ? 0 : 'auto',
              display: 'flex',
              flexDirection: isMediumSize ? 'column' : 'row',
              justifyContent: isMediumSize ? (isXsMidSize ? 'start' : 'center') : 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                {isEditMode ? (
                  <ProjectPicture
                    extension={extension}
                    avatar={avatar}
                    alt={`${projectDetails.project.name}--Project Avatar`}
                    editable={isEditMode}
                    updateAvatar={avatar => setValue('avatar', avatar)}
                    updateExtension={extension => setValue('extension', extension)}
                    onChange={updateViewValues}
                  />
                ) : (
                  !isEditMode && (
                    <AvatarComponent
                      extension={extension}
                      avatar={avatar}
                      alt={`${projectDetails.project.name}--Project Avatar`}
                      size={72}
                    />
                  )
                )}

                <Typography
                  component={'h2'}
                  variant="h5"
                  color="text.highEmphasis"
                  justifyContent="center"
                  alignItems="center"
                >
                  {`${projectDetails?.project.name} • ${projectDetails?.project.acronym}`}
                </Typography>
              </Box>
              <Box
                color="text.mediumEmphasis"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexWrap: isDownSm ? 'wrap' : 'none',
                  gap: isDownSm ? 0.5 : 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'none',
                    gap: 2,
                  }}
                >
                  <Box ref={hoursRef} display="flex" flexDirection="column" sx={{ position: 'relative' }}>
                    <Box display="flex" alignItems="center" gap="8px">
                      <WatchLaterOutlined />
                      <Typography>{`${totalHours}h`}</Typography>
                      {projectDetails.isParent && (
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation()
                            setShowHours(prev => !prev)
                          }}
                          sx={{ p: 0.5 }}
                        >
                          {showHours ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                      )}
                    </Box>
                    {showHours && (
                      <Paper
                        elevation={2}
                        sx={{
                          position: 'absolute',
                          left: 0,
                          top: '30px',
                          zIndex: 10,
                          minWidth: 200,
                          bgcolor: 'background.paper',
                          boxShadow: 3,
                          p: 2,
                          borderRadius: 2,
                          mt: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          minHeight: 40,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          cursor: 'default',
                        }}
                      >
                        <Stack pl={0} gap={0.5} mt={0.5}>
                          {Object.entries(projectDetails.hours).map(([acronym, hours]) => (
                            <Box key={acronym} display="flex" justifyContent="space-between">
                              <Typography variant="body2">{`${acronym}:`}</Typography>
                              <Typography variant="body2">{`${hours}h`}</Typography>
                            </Box>
                          ))}
                          <Box
                            mt={0.5}
                            pt={0.5}
                            borderTop="1px solid"
                            borderColor="divider"
                            display="flex"
                            justifyContent="space-between"
                          >
                            <Typography fontWeight="bold">{t('page.projectDetails.tiles.sum')}</Typography>
                            <Typography fontWeight="bold">{`${totalHours}h`}</Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    )}
                  </Box>
                  <Box display="flex" alignItems="center" gap="8px">
                    {projectDetails.project.privacy ? <Lock /> : <LockOpen />}
                    <Typography>
                      {projectDetails.project.privacy
                        ? t('page.projects.filterPopup.private')
                        : t('page.projects.filterPopup.public')}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'none',
                    gap: 4,
                  }}
                >
                  {projectDetails.isParent && (
                    <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                      <DeviceHub />
                      <Typography>{t('page.projectDetails.tiles.parentProject')}</Typography>
                    </Box>
                  )}
                  <Box sx={{ ml: -3 }}>
                    <IconButton
                      onClick={() => setProjectFavorite(!isFavorite)}
                      sx={{ color: 'primary.light' }}
                      aria-label={`favorite-icon-button-${isFavorite}`}
                    >
                      {isFavorite ? <Star fontSize={'large'} /> : <StarOutline fontSize={'large'} />}
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Stack gap={2} width={isXsMidSize ? '100%' : 'auto'}>
              {isLargeSize && (
                <>
                  <EmployeeContributionDrawer
                    resize={false}
                    fullWidth={false}
                    projectDetails={projectDetails}
                    isEditMode={isEditMode}
                    isNotLeaderOfProject={isNotLeaderOfProject}
                    selectedDate={selectedDate}
                    handleDateChange={handleDateChange}
                  />
                  <ProjectCostsDrawer
                    resize={false}
                    fullWidth={false}
                    projectId={projectDetails.project.id}
                    isEditMode={isEditMode}
                    isNotLeaderOfProject={isNotLeaderOfProject}
                    selectedDate={selectedDate}
                    handleDateChange={handleDateChange}
                  />
                </>
              )}
              <Box sx={{ display: 'flex', gap: 2, flexDirection: isXsMidSize ? 'column' : 'row' }}>
                {!isEditMode && projectDetails.project.operational && (
                  <Button
                    variant="contained"
                    color="secondary"
                    disableElevation
                    size="small"
                    onClick={() => setOpenDeleteDialog(true)}
                    startIcon={<Delete />}
                    disabled={
                      projectDetails.project.projectStatus === ProjectStatus.DELETED.toUpperCase() ||
                      isNotLeaderOfProject
                    }
                  >
                    {t('page.projects.actionPopup.delete')}
                  </Button>
                )}

                <Button
                  sx={{ display: !isEditMode ? 'none' : '' }}
                  type="button"
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={handleCancelButtonClick}
                  startIcon={<Clear />}
                  disabled={isSubmitting}
                >
                  {t('page.worklog.form.cancel')}
                </Button>

                <Button
                  sx={{ display: isEditMode ? 'none' : 'flex' }}
                  type="button"
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => {
                    setEditMode(!isEditMode)
                    setIsEditMode(!isEditMode)
                  }}
                  startIcon={<Edit />}
                  disabled={isNotLeaderOfProject}
                >
                  {t('page.usersProfile.header.edit')}
                </Button>

                <LoadingButton
                  sx={{ display: !isEditMode ? 'none' : '' }}
                  type="submit"
                  disabled={saveButtonDisabled || isSubmitting}
                  loading={isSubmitting}
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<Save />}
                >
                  {t('page.projects.editProject.saveProject')}
                </LoadingButton>
                {!isLargeSize && (
                  <>
                    <EmployeeContributionDrawer
                      resize={false}
                      fullWidth={false}
                      projectDetails={projectDetails}
                      isEditMode={isEditMode}
                      isNotLeaderOfProject={isNotLeaderOfProject}
                      selectedDate={selectedDate}
                      handleDateChange={handleDateChange}
                    />
                    <ProjectCostsDrawer
                      resize={false}
                      fullWidth={false}
                      projectId={projectDetails.project.id}
                      isEditMode={isEditMode}
                      isNotLeaderOfProject={isNotLeaderOfProject}
                      selectedDate={selectedDate}
                      handleDateChange={handleDateChange}
                    />
                  </>
                )}
              </Box>
            </Stack>
          </StyledPageHeaderContainer>

          <Grid height={'fit-content'} container spacing={1}>
            <Grid
              item
              xs={12}
              md={6}
              sx={{ p: 2, pr: isXsMidSize ? 0 : 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 2,
                }}
              >
                <Typography component={'h3'} variant="h5" color="text.mediumEmphasis">
                  {t('page.projects.actionPopup.details')}
                </Typography>
                {isEditMode && (
                  <Typography variant="caption">{t('page.usersProfile.form.headers.requiredFields')}</Typography>
                )}
              </Box>

              {isEditMode ? (
                <Stack direction={isLargeSize ? 'column' : 'row'} sx={{ gap: 2 }}>
                  <Stack direction="column" gap={2} width={'100%'}>
                    <TextInput
                      name={'name'}
                      control={control}
                      label={t('page.projects.editProject.fullName')}
                      placeholder={t('page.projects.editProject.fullName')}
                      maxLength={50}
                      required
                      rules={{
                        required: t('components.costSidebar.form.errors.amountRequired'),
                        maxLength: {
                          value: 50,
                          message: t('page.projects.editProject.nameLengthError'),
                        },
                      }}
                    />

                    <TextInput
                      name={'acronym'}
                      control={control}
                      label={t('components.table.header.acronym')}
                      placeholder={t('components.table.header.acronym')}
                      maxLength={20}
                      required
                      rules={{
                        required: t('page.projects.form.required.acronym'),
                        maxLength: {
                          value: 20,
                          message: t('page.projects.editProject.acronymLengthError'),
                        },
                      }}
                    />

                    <TextInput
                      name={'description'}
                      control={control}
                      label={t('components.table.header.description')}
                      placeholder={t('page.projects.editProject.enterDescription')}
                      maxLength={500}
                      rules={{
                        maxLength: {
                          value: 500,
                          message: t('page.worklog.form.descriptionLengthError'),
                        },
                      }}
                      multiline
                      rows={6}
                      showCharCounter
                    />

                    {!projectDetails.isParent && (
                      <AutocompleteInput
                        options={displayedProjects.map(project => ({
                          label: `${project.acronym}`,
                          value: project.id,
                        }))}
                        name={'parent'}
                        control={control}
                        label={t('page.projects.editProject.parentProject')}
                        onSelectBottomScroll={onSelectBottomScroll}
                        handleInputChange={handleInputChange}
                        filterOptions={(options, state) => filterOptions(options, state, displayedProjects)}
                      />
                    )}

                    <TextInput
                      name={'driveUrl'}
                      control={control}
                      label={t('page.projects.editProject.driveUrl')}
                      rules={{
                        maxLength: { value: 100, message: t('page.projects.form.driveUrlMax', { value: 100 }) },
                        validate: { isUrl },
                      }}
                      maxLength={100}
                    />

                    <SelectInput
                      options={projectStatusOptions}
                      name={'projectStatus'}
                      control={control}
                      required={true}
                      label={'Status'}
                      rules={{ required: t('page.projects.form.required.projectStatus') }}
                    />

                    <CheckboxInput
                      name={'privacy'}
                      control={control}
                      label={t('page.projects.form.privateProjectDescription')}
                    />

                    {isManagementOrAdministrator && (
                      <CheckboxInput
                        name={'operational'}
                        control={control}
                        label={t('page.projects.form.operational')}
                      />
                    )}

                    <CheckboxInput
                      name={'loggable'}
                      control={control}
                      label={t('page.projects.form.loggable')}
                      disabled={
                        watchProjectStatus !== ProjectStatus.ACTIVE && watchProjectStatus !== ProjectStatus.TRIAL
                      }
                    />
                  </Stack>
                  <Stack direction="column" gap={2} width={'100%'}>
                    <SelectProjectLeaders leaders={leaders} control={control} />
                  </Stack>
                </Stack>
              ) : (
                <Stack gap={2}>
                  <DetailsTile
                    title={t('page.projectDetails.tiles.fullName')}
                    description={projectDetails.project.name}
                    icon={<Badge />}
                  />
                  <DetailsTile
                    title={t('page.projectDetails.tiles.acronym')}
                    description={projectDetails.project.acronym}
                    icon={<Sell />}
                  />
                  <DetailsTile
                    title={t('page.projectDetails.tiles.description')}
                    description={projectDetails.project.description ? projectDetails.project.description : '-'}
                    icon={<Edit />}
                    wrap={true}
                  />
                  <DetailsTile
                    title={t('page.projectDetails.tiles.projectLeaders')}
                    description={
                      projectDetails.project.leaders
                        ? projectDetails.project.leaders
                            .slice()
                            .sort((a, b) => (b.main ? 1 : -1))
                            .map((leader, index) => (
                              <span key={leader.id}>
                                {leader.main ? (
                                  <strong>{`${leader.firstName} ${leader.lastName}`}</strong>
                                ) : (
                                  `${leader.firstName} ${leader.lastName}`
                                )}
                                {index < projectDetails.project.leaders.length - 1 && ', '}
                              </span>
                            ))
                        : '-'
                    }
                    icon={<Person />}
                  />
                  {!projectDetails.isParent && (
                    <DetailsTile
                      title={t('page.projectDetails.tiles.parentProject')}
                      description={projectDetails.project.parent ? projectDetails.project.parent.name : '-'}
                      icon={<SupervisorAccount />}
                    />
                  )}
                  <DetailsTile
                    title={t('page.projectDetails.tiles.driveUrl')}
                    description={
                      <a
                        style={{ color: 'inherit' }}
                        href={projectDetails.project.driveUrl || undefined}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {projectDetails.project.driveUrl || '-'}
                      </a>
                    }
                    icon={<AddToDrive />}
                  />
                  <DetailsTile
                    title={t('page.projectDetails.tiles.status')}
                    description={t(`page.projectDetails.status.${projectDetails.project.projectStatus}`)}
                    icon={<Settings />}
                  />
                  <DetailsTile
                    title={t('page.projectDetails.tiles.privacy')}
                    description={
                      projectDetails.project.privacy
                        ? t('page.projectDetails.privacy.private')
                        : t('page.projectDetails.privacy.public')
                    }
                    icon={<Lock />}
                  />
                  <DetailsTile
                    title={t('page.projectDetails.tiles.operational')}
                    description={
                      projectDetails.project.operational
                        ? t('page.projectDetails.operational.yes')
                        : t('page.projectDetails.operational.no')
                    }
                    icon={<BusinessCenterIcon />}
                  />
                  <DetailsTile
                    title={t('page.projectDetails.tiles.loggable')}
                    description={
                      projectDetails.project.loggable
                        ? t('page.projectDetails.loggable.yes')
                        : t('page.projectDetails.loggable.no')
                    }
                    icon={<AppRegistration />}
                  />
                </Stack>
              )}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              sx={{ p: 2, pr: isXsMidSize ? 0 : 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <StyledContainerRowBetween>
                <Typography component={'h3'} variant="h5" color="text.mediumEmphasis" id={'team-header'}>
                  {t('page.projects.detailsView.team')}
                </Typography>
              </StyledContainerRowBetween>
              <TeamTable
                editable={isEditMode}
                replace={replaceArray}
                watchedUsers={watch('team')}
                users={projectDetails.project.team}
                leaders={getValues('leaders')}
              />
            </Grid>
          </Grid>
        </form>
      )}
      <DeleteProjectDialog
        open={openDeleteDialog}
        projectName={getValues('name')}
        handleDelete={handleDelete}
        onClose={() => setOpenDeleteDialog(false)}
      />
      <DiscardChangesDialog
        open={isCancelDialogOpen}
        checkUnsavedChanges={checkUnsavedChanges}
        setIsCancelDialogOpen={setIsCancelDialogOpen}
        resetForm={resetForm}
      />
    </StyledPageContent>
  )
}

export default ProjectDetailsView
